import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';
import { SuggestionsAutoCompleteFieldItem } from '../store/api-slices/suggestions-autocomplete-docs.endpoints';
import { mapSuggestionFieldToFormField, isDisabledSuggestionField } from '../config/fieldConfigs';

export type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'LOW';

export interface FieldConfidenceInfo {
  level: ConfidenceLevel;
  borderColor: string;
  boxShadow: string;
  disabled: boolean;
}

/**
 * Hook que mapea cada campo del formulario a su nivel de confianza
 * basado en los datos del servicio de sugerencias (CO y MX)
 */
export const useFieldConfidence = (
  svcItems?: SuggestionsAutoCompleteFieldItem[]
): Record<string, FieldConfidenceInfo> => {
  const country = useSelector(getCountry);

  return useMemo(() => {
    // Aplicar niveles de confianza para CO y MX
    if (country !== CountryCode.CO && country !== CountryCode.MX) return {};
    if (!svcItems?.length) return {};

    const confidenceMap: Record<string, FieldConfidenceInfo> = {};

    svcItems.forEach(item => {
      if (!item.fieldConfidenceLevel) return;
      // Sin niveles de confianza para campos con sugerencias deshabilitadas
      if (isDisabledSuggestionField(item.field, country as CountryCode)) return;

      const level = item.fieldConfidenceLevel;
      let borderColor = '';
      let boxShadow = '';
      let disabled = false;

      switch (level) {
        case 'HIGH':
          borderColor = '#28a745';
          boxShadow = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';
          disabled = true;
          break;
        case 'MEDIUM':
          borderColor = '#ffc107';
          boxShadow = '0 0 0 0.2rem rgba(255, 193, 7, 0.25)';
          break;
        case 'LOW':
          borderColor = '#dc3545';
          boxShadow = '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
          break;
      }

      // Mapear nombre de campo de suggestions a formulario (ej: halfBathroom → halfBaths)
      const formFieldName = mapSuggestionFieldToFormField(item.field);
      confidenceMap[formFieldName] = {
        level,
        borderColor,
        boxShadow,
        disabled,
      };
    });

    return confidenceMap;
  }, [svcItems, country]);
};
